import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { PostgrestFilterBuilder } from '@supabase/postgrest-js';

/**
 * Hook for querying Supabase with automatic loading/error state.
 * Wrapper around Supabase queries with loading/error state.
 *
 * @param table - The Supabase table name
 * @param options - Query options (select, filters, order, etc.)
 * @returns { data, loading, error, refetch }
 */
export function useSupabaseQuery<T = any>(
  table: string,
  options?: {
    select?: string;
    filter?: (query: any) => any;
    order?: { column: string; ascending?: boolean };
    single?: boolean;
    enabled?: boolean;
  }
) {
  const enabled = options?.enabled !== false;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const selectStr = options?.select || '*';

  // Store options in refs to avoid stale closures
  const filterRef = options?.filter;
  const orderCol = options?.order?.column;
  const orderAsc = options?.order?.ascending ?? true;
  const single = options?.single;

  const fetchData = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setError('Tidsavbrudd ved henting av data');
      setLoading(false);
    }, 15000);

    try {
      let query = supabase.from(table).select(selectStr);

      if (filterRef) {
        query = filterRef(query);
      }

      if (orderCol) {
        query = query.order(orderCol, { ascending: orderAsc });
      }

      if (single) {
        const { data: result, error: err } = await query.single();
        if (err) throw err;
        setData(result as T);
      } else {
        const { data: result, error: err } = await query;
        if (err) throw err;
        setData(result as T);
      }
    } catch (err: any) {
      setError(err.message || 'Feil ved henting av data');
      setData(null);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, selectStr, enabled, single, orderCol, orderAsc]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for Supabase mutations (insert, update, delete).
 * Wrapper around Supabase mutations with loading/error state.
 *
 * @param table - The Supabase table name
 * @returns { insert, update, upsert, remove, loading, error }
 */
export function useSupabaseMutation<T = any>(table: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const insert = useCallback(async (values: Partial<T> | Partial<T>[]) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from(table)
        .insert(values as any)
        .select();

      if (err) throw err;
      return { data, error: null };
    } catch (err: any) {
      setError(err.message);
      return { data: null, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [table]);

  const update = useCallback(async (id: string, values: Partial<T>) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from(table)
        .update(values as any)
        .eq('id', id)
        .select()
        .single();

      if (err) throw err;
      return { data, error: null };
    } catch (err: any) {
      setError(err.message);
      return { data: null, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [table]);

  const upsert = useCallback(async (values: Partial<T> | Partial<T>[]) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from(table)
        .upsert(values as any)
        .select();

      if (err) throw err;
      return { data, error: null };
    } catch (err: any) {
      setError(err.message);
      return { data: null, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [table]);

  const remove = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { error: err } = await supabase
        .from(table)
        .delete()
        .eq('id', id);

      if (err) throw err;
      return { error: null };
    } catch (err: any) {
      setError(err.message);
      return { error: err.message };
    } finally {
      setLoading(false);
    }
  }, [table]);

  return { insert, update, upsert, remove, loading, error };
}

/**
 * Hook for realtime subscriptions on a Supabase table.
 * Provides live updates via Supabase realtime subscriptions.
 */
export function useSupabaseRealtime<T = any>(
  table: string,
  options?: {
    select?: string;
    filter?: (query: any) => any;
    order?: { column: string; ascending?: boolean };
    event?: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  }
) {
  const { data, loading, error, refetch } = useSupabaseQuery<T[]>(table, options);

  useEffect(() => {
    const channel = supabase
      .channel(`${table}_changes`)
      .on(
        'postgres_changes',
        {
          event: options?.event || '*',
          schema: 'public',
          table,
        },
        () => {
          // Refetch on any change
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [table, refetch]);

  return { data, loading, error, refetch };
}
